import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { 
  UserCommercialSettings, 
  UserCommercialFee, 
  SenderFeeOverride,
  UpdateCategoryFeeRequest,
  UpdateSenderFeeRequest,
  ResetCategoryFeesRequest 
} from '../types';

const CategorySettings: React.FC = () => {
  // State
  const [settings, setSettings] = useState<UserCommercialSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // Default fee editing
  const [defaultFee, setDefaultFee] = useState<string>('0.05');
  const [defaultFeeEdited, setDefaultFeeEdited] = useState(false);
  
  // Reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetNewFee, setResetNewFee] = useState<string>('0.05');
  const [resetAllCategories, setResetAllCategories] = useState(true);
  
  // Category edit dialog
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<UserCommercialFee | null>(null);
  const [editCategoryFee, setEditCategoryFee] = useState<string>('0.05');
  const [editCategoryRefuse, setEditCategoryRefuse] = useState(false);
  
  // Sender edit dialog
  const [editSenderOpen, setEditSenderOpen] = useState(false);
  const [editingSender, setEditingSender] = useState<SenderFeeOverride | null>(null);
  const [editSenderFee, setEditSenderFee] = useState<string>('0.05');
  const [editSenderRefuse, setEditSenderRefuse] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<UserCommercialSettings>('/api/commercialfees/settings');
      setSettings(data);
      setDefaultFee(data.defaultCommercialDeliveryFee.toString());
      // Also set reset fee to user's current default so Reset All uses their default
      setResetNewFee(data.defaultCommercialDeliveryFee.toString());
    } catch (err: any) {
      console.error('Failed to load commercial settings:', err);
      setError(err.response?.data?.error || 'Failed to load commercial fee settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDefaultFee = async () => {
    const fee = parseFloat(defaultFee);
    if (isNaN(fee) || fee < 0) {
      setError('Invalid default fee value');
      return;
    }
    
    setSaving(true);
    try {
      // Send as JSON object to ensure proper binding
      await api.put('/api/commercialfees/default', { defaultFee: fee });
      setSuccess('Default commercial delivery fee updated');
      setDefaultFeeEdited(false);
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error updating default fee:', err);
      setError(err.response?.data?.error || 'Failed to update default fee');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAllFees = async () => {
    const fee = parseFloat(resetNewFee);
    if (isNaN(fee) || fee < 0) {
      setError('Invalid fee value');
      return;
    }
    
    setSaving(true);
    try {
      const request: ResetCategoryFeesRequest = {
        newDefaultFee: fee,
        resetAllCategories: resetAllCategories
      };
      await api.post('/api/commercialfees/reset', request);
      setSuccess('All commercial fees have been reset');
      setResetDialogOpen(false);
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset fees');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategory = (category: UserCommercialFee) => {
    setEditingCategory(category);
    setEditCategoryFee(category.deliveryFee.toString());
    setEditCategoryRefuse(category.refuseMail);
    setEditCategoryOpen(true);
  };

  const handleSaveCategoryFee = async () => {
    if (!editingCategory) return;
    
    const fee = parseFloat(editCategoryFee);
    if (isNaN(fee) || fee < 0) {
      setError('Invalid fee value');
      return;
    }
    
    setSaving(true);
    try {
      const request: UpdateCategoryFeeRequest = {
        commercialCode: editingCategory.commercialCode,
        deliveryFee: fee,
        refuseMail: editCategoryRefuse
      };
      await api.put('/api/commercialfees/category', request);
      setSuccess(`Updated fee for category "${editingCategory.description || editingCategory.commercialCode}"`);
      setEditCategoryOpen(false);
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update category fee');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategoryFee = async (code: string) => {
    if (!confirm('Revert this category to the default fee?')) return;
    
    setSaving(true);
    try {
      await api.delete(`/api/commercialfees/category/${encodeURIComponent(code)}`);
      setSuccess('Category fee reverted to default');
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete category fee');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSender = (sender: SenderFeeOverride) => {
    setEditingSender(sender);
    setEditSenderFee(sender.deliveryFee.toString());
    setEditSenderRefuse(sender.refuseMail);
    setEditSenderOpen(true);
  };

  const handleSaveSenderFee = async () => {
    if (!editingSender) return;
    
    const fee = parseFloat(editSenderFee);
    if (isNaN(fee) || fee < 0) {
      setError('Invalid fee value');
      return;
    }
    
    setSaving(true);
    try {
      const request: UpdateSenderFeeRequest = {
        senderAddress: editingSender.senderAddress,
        deliveryFee: fee,
        refuseMail: editSenderRefuse
      };
      await api.put('/api/commercialfees/sender', request);
      setSuccess(`Updated fee for sender "${editingSender.senderAddress}"`);
      setEditSenderOpen(false);
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update sender fee');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSenderFee = async (address: string) => {
    if (!confirm('Remove this sender override?')) return;
    
    setSaving(true);
    try {
      await api.delete(`/api/commercialfees/sender/${encodeURIComponent(address)}`);
      setSuccess('Sender fee override removed');
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete sender fee');
    } finally {
      setSaving(false);
    }
  };

  // Group categories by their category name
  const groupedCategories = settings?.categoryFees.reduce((acc, fee) => {
    const category = fee.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(fee);
    return acc;
  }, {} as Record<string, UserCommercialFee[]>) || {};

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CategoryIcon color="primary" />
        Category Settings
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage delivery fees for commercial/marketing messages. Set a default fee for all marketing emails, 
        then customize fees for specific categories or individual senders.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Default Commercial Fee Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" />
          Default Marketing Delivery Fee
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This fee applies to all commercial/marketing messages unless a specific category or sender override is set.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Default Fee"
            type="number"
            value={defaultFee}
            onChange={(e) => {
              setDefaultFee(e.target.value);
              setDefaultFeeEdited(true);
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ width: 150 }}
            size="small"
          />
          
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveDefaultFee}
            disabled={saving || !defaultFeeEdited}
          >
            Save Default
          </Button>
          
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={() => {
              // Pre-populate with user's current default fee
              if (settings) {
                setResetNewFee(settings.defaultCommercialDeliveryFee.toString());
              }
              setResetDialogOpen(true);
            }}
          >
            Reset All Fees
          </Button>
        </Box>
      </Paper>

      {/* Tabs for Category Fees and Sender Overrides */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon fontSize="small" />
                Category Fees
                {settings?.categoryFees.filter(f => !f.isDefault).length ? (
                  <Chip 
                    label={settings.categoryFees.filter(f => !f.isDefault).length} 
                    size="small" 
                    color="primary"
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                ) : null}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" />
                Sender Overrides
                {settings?.senderFees.length ? (
                  <Chip 
                    label={settings.senderFees.length} 
                    size="small" 
                    color="secondary"
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                ) : null}
              </Box>
            } 
          />
        </Tabs>

        {/* Category Fees Tab Panel */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set different delivery fees for specific commercial categories. Categories using the default fee are shown in gray.
            </Typography>
            
            {Object.keys(groupedCategories).length === 0 ? (
              <Alert severity="info">No commercial categories found.</Alert>
            ) : (
              Object.entries(groupedCategories).map(([categoryName, fees]) => (
                <Accordion key={categoryName} defaultExpanded={fees.some(f => !f.isDefault)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 'medium' }}>
                      {categoryName}
                      <Chip 
                        label={`${fees.length} codes`} 
                        size="small" 
                        sx={{ ml: 1 }}
                        color={fees.some(f => !f.isDefault) ? 'primary' : 'default'}
                      />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Code</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Delivery Fee</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fees.map((fee) => (
                            <TableRow 
                              key={fee.commercialCode}
                              sx={{ 
                                opacity: fee.isDefault ? 0.7 : 1,
                                bgcolor: fee.refuseMail ? 'error.lighter' : 'inherit'
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {fee.commercialCode}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {fee.description || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  fontWeight={fee.isDefault ? 'normal' : 'bold'}
                                  color={fee.isDefault ? 'text.secondary' : 'primary'}
                                >
                                  ${fee.deliveryFee.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {fee.refuseMail ? (
                                  <Chip label="Blocked" size="small" color="error" icon={<BlockIcon />} />
                                ) : fee.isDefault ? (
                                  <Chip label="Default" size="small" variant="outlined" />
                                ) : (
                                  <Chip label="Custom" size="small" color="primary" />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Edit fee">
                                  <IconButton size="small" onClick={() => handleEditCategory(fee)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {!fee.isDefault && (
                                  <Tooltip title="Revert to default">
                                    <IconButton 
                                      size="small" 
                                      color="warning"
                                      onClick={() => handleDeleteCategoryFee(fee.commercialCode)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        )}

        {/* Sender Overrides Tab Panel */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Override delivery fees for specific senders. These take priority over category fees.
              You can also set these when viewing a message and clicking "Adjust Sender Rate".
            </Typography>
            
            {settings?.senderFees.length === 0 ? (
              <Alert severity="info">
                No sender-specific overrides set. You can add them when viewing a message and clicking "Adjust Sender Rate".
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sender Address</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Delivery Fee</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settings?.senderFees.map((sender) => (
                      <TableRow 
                        key={sender.senderAddress}
                        sx={{ bgcolor: sender.refuseMail ? 'error.lighter' : 'inherit' }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {sender.senderAddress}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sender.senderName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            ${sender.deliveryFee.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {sender.refuseMail ? (
                            <Chip label="Blocked" size="small" color="error" icon={<BlockIcon />} />
                          ) : (
                            <Chip label="Custom" size="small" color="primary" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit fee">
                            <IconButton size="small" onClick={() => handleEditSender(sender)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove override">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteSenderFee(sender.senderAddress)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Reset Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset All Commercial Fees</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will update the default marketing delivery fee and optionally reset all category-specific fees.
          </Typography>
          
          <TextField
            fullWidth
            label="New Default Fee"
            type="number"
            value={resetNewFee}
            onChange={(e) => setResetNewFee(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={resetAllCategories} 
                onChange={(e) => setResetAllCategories(e.target.checked)}
              />
            }
            label="Reset all category-specific fees to this new default"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="warning" 
            onClick={handleResetAllFees}
            disabled={saving}
          >
            Reset Fees
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editCategoryOpen} onClose={() => setEditCategoryOpen(false)}>
        <DialogTitle>
          Edit Category Fee: {editingCategory?.commercialCode}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editingCategory?.description || 'No description'}
          </Typography>
          
          <TextField
            fullWidth
            label="Delivery Fee"
            type="number"
            value={editCategoryFee}
            onChange={(e) => setEditCategoryFee(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={editCategoryRefuse} 
                onChange={(e) => setEditCategoryRefuse(e.target.checked)}
                color="error"
              />
            }
            label="Block all messages from this category"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCategoryOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveCategoryFee}
            disabled={saving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sender Dialog */}
      <Dialog open={editSenderOpen} onClose={() => setEditSenderOpen(false)}>
        <DialogTitle>
          Edit Sender Override: {editingSender?.senderAddress}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Delivery Fee"
            type="number"
            value={editSenderFee}
            onChange={(e) => setEditSenderFee(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2, mt: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={editSenderRefuse} 
                onChange={(e) => setEditSenderRefuse(e.target.checked)}
                color="error"
              />
            }
            label="Block all messages from this sender"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSenderOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveSenderFee}
            disabled={saving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategorySettings;

